// ---------------------------------------------------------------------------
// leadsHeute — was auf der Leads-Seite HEUTE zu tun ist (LEAD-25). Reine
// Funktion.
// ---------------------------------------------------------------------------
// Daniel, 08.10.2026: «die leads seite braucht definitiv einen rewrite - sie
// darf für marvin als koordinator auch anders aussehen wie für corsin und
// Sandro. wir bruachen eine bessere übersicht und nicht so viel infos auf
// einmal das erschlägt einem ja».
//
// Gemessen am 08.10.2026 (Produktion, je Person per Vertretung): die Seite
// zeigte jedem alle 69 offenen Anfragen als Karten-Zeilen — Marvin dazu 47
// Meldungs-Zeilen mit Knopf-Leisten, Sandro 43, Corsin 19. Nach DIESER Regel
// stehen zuoberst: Marvin 4 «Heute» und 3 «Verteilen», Corsin und Sandro je
// 0–1. Der Rest liegt einen Klick tiefer und bleibt vollstaendig auffindbar.
//
// Die Regel ordnet jede offene Anfrage fuer EINEN Betrachter in GENAU EINEN
// Korb — mit einem benannten Grund (Muster `shared/roof/dispatch.ts`): eine
// Seite, die Arbeit weglegt, muss zu jeder Anfrage sagen koennen, warum sie
// dort liegt, wo sie liegt.
//
//   heute       — heute anfassen: eine Frist laeuft, etwas ist in den letzten
//                 zwei Arbeitstagen passiert, oder ein Richtpreis liegt bereit
//   verteilen   — braucht einen Projektleiter (nur Koordinator-Sicht; wer
//                 sonst verteilen darf, findet es unter «Heute»)
//   nachfassen  — meine, in Bewegung, aber heute nicht faellig
//   aufraeumen  — seit Wochen ohne Bewegung: retten oder schliessen
//   alle        — steht nur in der Tabelle «Alle»: nicht meine, oder ruhend
//
// «Bewegung» ist, was ein Mensch getan hat: Eingang, Zuweisung, Erstkontakt,
// eine Mail von uns, eine Antwort des Kunden. Bewusst NICHT: der Zeitpunkt,
// zu dem ein Richtpreis-Dokument neu gerechnet wurde, und Belege aus bexio —
// beide werden in Stapeln neu gestempelt (gemessen 07.10.2026: drei
// Richtpreis-Entwuerfe zu Anfragen vom Juli und fuenf bexio-Belege tragen
// dasselbe Datum) und wuerden alte Anfragen als «heute» ausgeben.
//
// Die Karten bleiben, was sie sind (materialisiert, Push, «Jetzt dran»,
// Waechter `karten-wahrheit`). Diese Regel liest sie nur.
//
// Pure TS, nur Typ-Importe aus dem Meldungs-Apparat — die Datei liegt im
// Startbuendel (die Leiste zaehlt mit ihr).

import { workdaysBetween } from "../utils/workdays";

import { LEAD_CRITICAL_WORKDAYS } from "./leadAge";
import {
  istLeadsMenueKarte,
  leadKarteProjektId,
  type LeadKarteLike,
  type OpenLeadItem,
} from "./leadsMenue";

/** Bis zu so vielen Arbeitstagen seit der letzten Bewegung gilt «heute». */
export const HEUTE_ARBEITSTAGE = 2;

/**
 * Ab so vielen Arbeitstagen ohne Bewegung liegt eine Anfrage beim Aufraeumen —
 * dieselbe Schwelle wie die Karte «Lead kritisch», aber an der letzten
 * BEWEGUNG gemessen statt am Eingang.
 */
export const AUFRAEUM_ARBEITSTAGE = LEAD_CRITICAL_WORKDAYS;

/** Koordinator: nimmt entgegen, verteilt, raeumt auf. PL: nur die eigenen. */
export type LeadSicht = "koordinator" | "pl";

export const LEAD_KOERBE = ["heute", "verteilen", "nachfassen", "aufraeumen"] as const;
export type LeadKorb = (typeof LEAD_KOERBE)[number];

/** Warum. Immer gesetzt, immer aus dieser Liste. */
export type LeadLageGrund =
  /** heute: gekauft, noch ohne Kontakt, die 24-h-Frist laeuft. */
  | "frist_laeuft"
  /** heute: ein Richtpreis liegt fertig bereit und wartet auf den Versand. */
  | "richtpreis_bereit"
  /** heute: der Richtpreis ist raus, das Nachfass-Datum ist erreicht. */
  | "nachfassen_faellig"
  /** heute: der Kunde hat sich in den letzten zwei Arbeitstagen gemeldet. */
  | "kunde_hat_geantwortet"
  /** heute: in den letzten zwei Arbeitstagen eingegangen oder zugewiesen. */
  | "neu_eingegangen"
  /** heute: Erstkontakt in den letzten zwei Arbeitstagen — jetzt der Richtpreis. */
  | "frisch_im_kontakt"
  /**
   * verteilen: ohne Projektleiter, jemand muss uebernehmen. In der
   * Projektleiter-Sicht liegt dasselbe unter «Heute» (Eskalation an Sandro).
   */
  | "braucht_projektleiter"
  /** nachfassen: der Richtpreis ist raus, das Nachfass-Datum liegt noch vor uns. */
  | "richtpreis_draussen"
  /** nachfassen: noch kein Kontakt, aber nicht mehr von heute. */
  | "wartet_auf_erstkontakt"
  /** nachfassen: im Kontakt, der Richtpreis steht noch aus. */
  | "richtpreis_offen"
  /** nachfassen: seit Wochen still, aber bewusst behalten (Merker `lead_keep`). */
  | "bewusst_behalten"
  /** aufraeumen: seit Wochen ohne Bewegung. */
  | "seit_wochen_still"
  /** alle: seit einem halben Jahr ohne Beleg (DATEN-3). */
  | "ruhend"
  /** alle: gehoert jemand anderem. */
  | "nicht_meine";

/** Der EINE naechste Handgriff — was der Knopf der Zeile tut. */
export type LeadHandgriff =
  | "zuweisen"
  | "erstkontakt"
  | "richtpreis_pruefen"
  | "richtpreis_erstellen"
  | "nachfassen"
  | "oeffnen";

export const LEAD_HANDGRIFF_LABEL: Record<LeadHandgriff, string> = {
  zuweisen: "Zuweisen…",
  erstkontakt: "Erstkontakt…",
  richtpreis_pruefen: "Richtpreis prüfen",
  richtpreis_erstellen: "Richtpreis erstellen",
  nachfassen: "Nachfassen",
  oeffnen: "Öffnen",
};

export interface LeadLage {
  /** `null` = steht nur unter «Alle». */
  readonly korb: LeadKorb | null;
  readonly grund: LeadLageGrund;
  readonly handgriff: LeadHandgriff;
  /** Arbeitstage seit der letzten Bewegung. */
  readonly stilleArbeitstage: number;
}

export interface LeadBetrachter {
  readonly userId: string;
  readonly sicht: LeadSicht;
}

/** Was eine Karte fuer diese Regel zusaetzlich hergeben kann. */
export interface LeadHeuteKarte extends LeadKarteLike {
  readonly assignedAt?: unknown;
  readonly contactedAt?: unknown;
  readonly followUpBy?: unknown;
  readonly signal?: unknown;
}

const VERTEIL_KARTEN: ReadonlySet<string> = new Set([
  "project_new_from_website",
  "lead_pl_needed",
]);

/** Belege, die ein Mensch erzeugt hat — nur sie zaehlen als Bewegung. */
const BEWEGUNGS_BELEGE: ReadonlySet<string> = new Set(["team_mail", "customer_reply"]);

function zeit(wert: unknown): number | null {
  if (typeof wert !== "string") return null;
  const ms = new Date(wert).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function signalZeit(karte: LeadHeuteKarte): number | null {
  const signal = karte.signal;
  if (signal === null || typeof signal !== "object") return null;
  return zeit((signal as { occurredAt?: unknown }).occurredAt);
}

/** Bin ich fuer diese Anfrage zustaendig? PL — oder Ansprechpartner, solange kein PL gesetzt ist. */
export function istMeineAnfrage(
  lead: Pick<OpenLeadItem, "ownerUserId" | "contactUserId">,
  userId: string,
): boolean {
  if (lead.ownerUserId !== null) return lead.ownerUserId === userId;
  return lead.contactUserId === userId;
}

/** Wann hat sich zuletzt ein Mensch bewegt? Nie vor dem Eingang. */
export function letzteBewegung(
  lead: Pick<OpenLeadItem, "createdAt" | "contactEvidence">,
  karten: readonly LeadHeuteKarte[],
): Date {
  let max = zeit(lead.createdAt) ?? 0;
  const beleg = lead.contactEvidence;
  if (beleg !== null && BEWEGUNGS_BELEGE.has(beleg.source)) {
    max = Math.max(max, zeit(beleg.occurredAt) ?? 0);
  }
  for (const karte of karten) {
    max = Math.max(
      max,
      zeit(karte.assignedAt) ?? 0,
      zeit(karte.contactedAt) ?? 0,
      signalZeit(karte) ?? 0,
    );
  }
  return new Date(max);
}

function naechsterHandgriff(
  lead: Pick<OpenLeadItem, "status">,
  typen: ReadonlySet<string>,
): LeadHandgriff {
  if (typen.has("roof_insight_draft")) return "richtpreis_pruefen";
  if (typen.has("roof_auto_sent_followup")) return "nachfassen";
  if (lead.status === "lead") return "erstkontakt";
  if (typen.has("lead_decide_pending")) return "richtpreis_erstellen";
  return "oeffnen";
}

/**
 * Wo liegt diese Anfrage fuer diesen Betrachter — und warum?
 *
 * `karten` sind MEINE Karten aus dem Leads-Menue zu dieser Anfrage;
 * `zuVerteilen` sagt, ob sie in der Liste «Wer übernimmt?» steht (derselbe
 * Endpunkt wie die bisherige Kachel — traegt auch Sandros Eskalations-Sicht,
 * fuer die es keine eigene Karte gibt).
 *
 * Vorrang von oben nach unten; jede Bedingung ist eine eigene, benannte.
 */
export function leadLage(
  lead: OpenLeadItem,
  karten: readonly LeadHeuteKarte[],
  betrachter: LeadBetrachter,
  zuVerteilen: boolean,
  now: Date,
): LeadLage {
  const typen = new Set(karten.map((k) => k.type));
  const stilleArbeitstage = workdaysBetween(letzteBewegung(lead, karten), now);
  const handgriff = naechsterHandgriff(lead, typen);
  const lage = (korb: LeadKorb | null, grund: LeadLageGrund, griff = handgriff): LeadLage => ({
    korb,
    grund,
    handgriff: griff,
    stilleArbeitstage,
  });

  if (lead.ruhend) return lage(null, "ruhend", "oeffnen");

  const meine = istMeineAnfrage(lead, betrachter.userId);

  if (betrachter.sicht === "koordinator") {
    const brauchtPl =
      lead.ownerUserId === null &&
      (zuVerteilen || karten.some((k) => VERTEIL_KARTEN.has(k.type)));
    if (brauchtPl) return lage("verteilen", "braucht_projektleiter", "zuweisen");
    // Eine fremde Anfrage steht beim Koordinator nur, wenn eine Karte sie
    // ihm in die Hand gibt (Eskalation).
    if (!meine && karten.length === 0) return lage(null, "nicht_meine", "oeffnen");
  } else {
    // Projektleiter-Sicht: «Verteilen» gibt es hier nicht — aber was die Liste
    // «Wer übernimmt?» MIR zeigt, darf nicht still werden. Bei Sandro steht
    // dort nur, was eskaliert ist (LEAD-10: die Frist ist gerissen); das ist
    // seine Sache von heute, mit dem Knopf zum Zuweisen.
    if (zuVerteilen && lead.ownerUserId === null) {
      return lage("heute", "braucht_projektleiter", "zuweisen");
    }
    if (!meine) return lage(null, "nicht_meine", "oeffnen");
  }

  // NACHFASS-1: der Automat hat den Richtpreis versendet, der Vorgang steht
  // auf «Angebot» und hat noch keinen Projektleiter. Er bleibt unter
  // «Nachfassen», bis jemand uebernimmt oder der Kunde reagiert (dann faellt
  // er aus der Liste, `stehtZumNachfassenNachAutomat`). Faellig wird er an dem
  // Datum, das die Karte «Richtpreis raus» traegt — auch nach «Verstanden»
  // (ohne Karte) steht er weiter hier, nie nirgends.
  if (lead.status === "offered") {
    const karte = karten.find((k) => k.type === "roof_auto_sent_followup");
    const faelligAm = karte ? zeit(karte.followUpBy) : null;
    return faelligAm !== null && faelligAm <= now.getTime()
      ? lage("heute", "nachfassen_faellig", "nachfassen")
      : lage("nachfassen", "richtpreis_draussen", "nachfassen");
  }

  const ohneKontakt = lead.status === "lead" && lead.contactEvidence === null;
  if (lead.purchased && ohneKontakt && !lead.deadlineMissed) {
    return lage("heute", "frist_laeuft");
  }

  if (stilleArbeitstage >= AUFRAEUM_ARBEITSTAGE) {
    // «Behalten» nimmt die Anfrage aus dem Stapel, bis sich etwas bewegt —
    // sie bleibt bei «Nachfassen» sichtbar, nie nirgends.
    return lead.behalten === true
      ? lage("nachfassen", "bewusst_behalten")
      : lage("aufraeumen", "seit_wochen_still");
  }

  if (typen.has("roof_insight_draft")) return lage("heute", "richtpreis_bereit");

  const nachfass = karten.find((k) => k.type === "roof_auto_sent_followup");
  if (nachfass) {
    const faellig = zeit(nachfass.followUpBy);
    return faellig !== null && faellig <= now.getTime()
      ? lage("heute", "nachfassen_faellig")
      : lage("nachfassen", "richtpreis_draussen");
  }

  if (stilleArbeitstage <= HEUTE_ARBEITSTAGE) {
    if (lead.contactEvidence?.source === "customer_reply") {
      return lage("heute", "kunde_hat_geantwortet");
    }
    return lead.status === "lead"
      ? lage("heute", "neu_eingegangen")
      : lage("heute", "frisch_im_kontakt");
  }

  return lead.status === "lead"
    ? lage("nachfassen", "wartet_auf_erstkontakt")
    : lage("nachfassen", "richtpreis_offen");
}

// ---------------------------------------------------------------------------
// Die Uebersicht: jede Anfrage in genau einem Korb, jede Karte auffindbar
// ---------------------------------------------------------------------------

export interface LeadHeuteZeile<K extends LeadHeuteKarte> {
  readonly lead: OpenLeadItem;
  /** Meine Karten zu dieser Anfrage — mit ihren Knoepfen, fuer das Detail. */
  readonly karten: readonly K[];
  readonly lage: LeadLage;
}

export interface LeadHeuteUebersicht<K extends LeadHeuteKarte> {
  readonly koerbe: Readonly<Record<LeadKorb, readonly LeadHeuteZeile<K>[]>>;
  /** JEDE offene Anfrage, neueste zuerst — die Tabelle «Alle». */
  readonly alle: readonly LeadHeuteZeile<K>[];
  /**
   * Karten aus dem Leads-Menue, deren Anfrage nicht in der Liste steht (schon
   * offeriert, noch nicht geladen). Sie stehen unter «Alle» — nie nirgends.
   */
  readonly ohneAnfrage: readonly K[];
}

/** Reihenfolge im Korb «Heute»: was eine Uhr hat, zuerst. */
const HEUTE_RANG: Partial<Record<LeadLageGrund, number>> = {
  frist_laeuft: 0,
  braucht_projektleiter: 1,
  kunde_hat_geantwortet: 1,
  nachfassen_faellig: 2,
  richtpreis_bereit: 3,
  neu_eingegangen: 4,
  frisch_im_kontakt: 5,
};

function eingang(zeile: LeadHeuteZeile<LeadHeuteKarte>): number {
  return new Date(zeile.lead.createdAt).getTime();
}

export function buildLeadHeuteUebersicht<K extends LeadHeuteKarte>(
  leads: readonly OpenLeadItem[],
  karten: readonly K[],
  betrachter: LeadBetrachter,
  zuVerteilenIds: ReadonlySet<string>,
  now: Date,
): LeadHeuteUebersicht<K> {
  const bekannt = new Set(leads.map((l) => l.projectId));
  const kartenJeProjekt = new Map<string, K[]>();
  const ohneAnfrage: K[] = [];
  for (const karte of karten) {
    if (!istLeadsMenueKarte(karte.type)) continue;
    const projectId = leadKarteProjektId(karte);
    if (projectId === null || !bekannt.has(projectId)) {
      ohneAnfrage.push(karte);
      continue;
    }
    const liste = kartenJeProjekt.get(projectId) ?? [];
    liste.push(karte);
    kartenJeProjekt.set(projectId, liste);
  }

  const koerbe: Record<LeadKorb, LeadHeuteZeile<K>[]> = {
    heute: [],
    verteilen: [],
    nachfassen: [],
    aufraeumen: [],
  };
  const alle: LeadHeuteZeile<K>[] = [];
  for (const lead of leads) {
    const meineKarten = kartenJeProjekt.get(lead.projectId) ?? [];
    const zeile: LeadHeuteZeile<K> = {
      lead,
      karten: meineKarten,
      lage: leadLage(
        lead,
        meineKarten,
        betrachter,
        zuVerteilenIds.has(lead.projectId),
        now,
      ),
    };
    alle.push(zeile);
    if (zeile.lage.korb !== null) koerbe[zeile.lage.korb].push(zeile);
  }

  koerbe.heute.sort(
    (a, b) =>
      (HEUTE_RANG[a.lage.grund] ?? 9) - (HEUTE_RANG[b.lage.grund] ?? 9) ||
      eingang(b) - eingang(a),
  );
  // Verteilen: wer am laengsten wartet, zuerst — dort reisst die Frist.
  koerbe.verteilen.sort((a, b) => eingang(a) - eingang(b));
  // Nachfassen: was am laengsten still ist, zuerst.
  koerbe.nachfassen.sort(
    (a, b) => b.lage.stilleArbeitstage - a.lage.stilleArbeitstage || eingang(a) - eingang(b),
  );
  // Aufraeumen: die aeltesten zuerst — der Stapel wird von unten kleiner.
  koerbe.aufraeumen.sort((a, b) => eingang(a) - eingang(b));
  alle.sort((a, b) => eingang(b) - eingang(a));

  return { koerbe, alle, ohneAnfrage };
}

// ---------------------------------------------------------------------------
// Wer sieht was
// ---------------------------------------------------------------------------

/**
 * Welche Sichten hat dieser Mensch — und welche ist sein Standard?
 *
 * Koordinator ist, wer in `lead.default_contact_user_id` steht (heute Marvin)
 * — keine neue Rolle, kein Name im Code. Wer Leads verteilen darf (Sandro),
 * kann auf die Koordinator-Sicht umschalten, startet aber als Projektleiter.
 */
export function leadSichten(
  user: { readonly id: string; readonly mayDistributeLeads: boolean },
  koordinatorUserId: string | null,
): { readonly standard: LeadSicht; readonly waehlbar: readonly LeadSicht[] } {
  if (user.id === koordinatorUserId) {
    return { standard: "koordinator", waehlbar: ["koordinator", "pl"] };
  }
  if (user.mayDistributeLeads) {
    return { standard: "pl", waehlbar: ["pl", "koordinator"] };
  }
  return { standard: "pl", waehlbar: ["pl"] };
}

/** Die Koerbe einer Sicht, in der Reihenfolge der Umschalter. */
export function koerbeDerSicht(sicht: LeadSicht): readonly LeadKorb[] {
  return sicht === "koordinator"
    ? ["heute", "verteilen", "nachfassen", "aufraeumen"]
    : ["heute", "nachfassen", "aufraeumen"];
}

export const LEAD_KORB_LABEL: Record<LeadKorb, string> = {
  heute: "Heute",
  verteilen: "Verteilen",
  nachfassen: "Nachfassen",
  aufraeumen: "Aufräumen",
};

/**
 * Die Zahl am Menuepunkt: was HEUTE auf mich wartet. Beim Koordinator zaehlt
 * «Verteilen» mit — eine Anfrage ohne Projektleiter ist seine Sache von heute.
 * Dazu die Auffang-Karten (in Produktion gemessen: 0).
 */
export function leadsHeuteZahl(
  uebersicht: LeadHeuteUebersicht<LeadHeuteKarte>,
  sicht: LeadSicht,
): number {
  return (
    uebersicht.koerbe.heute.length +
    (sicht === "koordinator" ? uebersicht.koerbe.verteilen.length : 0) +
    // Eine Karte fuer mich, deren Anfrage nicht in der Liste steht, darf nicht
    // still werden — sie zaehlt mit und steht unter «Alle».
    uebersicht.ohneAnfrage.length
  );
}
